import React, { useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { RecurlyProvider, Elements } from '@recurly/react-recurly';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@nebula/hooks';
import { ThemeContext } from 'styled-components';

import { TitleMedium500, TitleSmall500 } from '@nebula/global-styles';
import PaymentCardForm from '../PaymentCaptureMethod/PaymentCardForm';
import {
  PaymentDivider,
  PaymentMethod,
  PaymentSection,
  Spinner,
} from '../PaymentCaptureMethod/PaymentMethodEntry/index.styles';
import { getEnv } from '../Context/env';
import paymentMethodChangeTransition from '../PaymentOrchestrator/transitions/paymentMethodChangeTransition';
import { PaymentContext } from '../PaymentOrchestrator/PaymentContextProvider';
import {
  Alert,
  ApplePayOption,
  GooglePayOption,
  LargePaymentChangeTitle,
  MethodUpdateButton,
  PaymentChangeButtons,
  PaymentInformationContainer,
  StandardPaymentChangeTitle,
} from './index.styles';
import { PAYMENT_ERROR_GROUPS, PAYMENT_ERROR_TYPES } from '../PaymentOrchestrator/constants/paymentErrors';
import PaymentError from '../PaymentCaptureMethod/PaymentError';
import { trackPaymentMethodChangeCancel, trackPaymentMethodChangeError } from '../../helpers/switch/analytics';
import ApplePay from '../PaymentCaptureMethod/PaymentMethodEntry/ApplePay';
import GooglePay from '../PaymentCaptureMethod/PaymentMethodEntry/GooglePay';
import useExistingPaymentMethod from '../PaymentCaptureMethod/hooks/useExistingPaymentMethod';
import { PaymentMethodTypes } from '../PaymentCaptureMethod/constants.ts';
import usePaymentMethods from '../PaymentOrchestrator/hooks/usePaymentMethods';

const PaymentMethodChange = ({ forceShow = false }) => {
  const { REACT_APP_RECURLY_PUBLIC_KEY: key } = getEnv();

  const paymentContext = useContext(PaymentContext);
  const { clearPaymentErrors, raisePaymentError, paymentErrors, paymentChangeActive, setPaymentChangeActive } =
    paymentContext;

  const dispatch = useDispatch();
  const formRef = useRef(null);
  const errorRef = useRef(null);

  const [processingMethodUpdate, setProcessingMethodUpdate] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const theme = useContext(ThemeContext);
  const useLargerCopy = useMediaQuery(theme.media.tabletPortraitMin);
  const { existingPaymentType } = useExistingPaymentMethod();

  const ChangePaymentTitle = useLargerCopy ? LargePaymentChangeTitle : StandardPaymentChangeTitle;
  const CardOptionTitle = useLargerCopy ? TitleMedium500 : TitleSmall500;
  const availablePaymentMethods = usePaymentMethods();
  const showApplePay = availablePaymentMethods.includes(PaymentMethodTypes.ApplePay);
  const showGooglePay = availablePaymentMethods.includes(PaymentMethodTypes.GooglePay);

  const handleFormValidityChange = isValid => setIsFormValid(isValid);

  const customer = useSelector(state => state.auth?.currentUser);
  const address = customer?.address?.[0];

  const handleCancel = () => {
    trackPaymentMethodChangeCancel();
    clearPaymentErrors();
    setProcessingMethodUpdate(false);
    setPaymentChangeActive(false);
  };

  const handleSubmit = () => {
    clearPaymentErrors();
    setProcessingMethodUpdate(true);
  };

  const onMethodChangeAvailable = node => {
    if (node && !forceShow) {
      node.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSuccess = async (paymentMethodToken, newPaymentType) => {
    const runBeforeTransition = () => setProcessingMethodUpdate(false);

    const updatedPaymentMethodType =
      Object.values(PaymentMethodTypes).find(paymentType => newPaymentType.includes(paymentType)) ?? 'unknown';

    const methodChangeAnalytics = `${existingPaymentType}->${updatedPaymentMethodType}`.toLowerCase();

    await paymentMethodChangeTransition(
      paymentContext,
      paymentMethodToken.id,
      methodChangeAnalytics,
      runBeforeTransition
    )(dispatch);
  };

  const handleFailure = error => {
    trackPaymentMethodChangeError(error.message);
    raisePaymentError({
      message: error.message,
      group: PAYMENT_ERROR_GROUPS.METHOD_CHANGE,
      type: PAYMENT_ERROR_TYPES.METHOD_CHANGE.UNSUCCESSFUL,
    });
    setProcessingMethodUpdate(false);
    errorRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  if (!paymentChangeActive && !forceShow) return null;

  const paymentError = paymentErrors.length ? paymentErrors[paymentErrors.length - 1] : null;

  return (
    <PaymentInformationContainer>
      {(paymentChangeActive || forceShow) && (
        <div ref={onMethodChangeAvailable}>
          <ChangePaymentTitle>{forceShow ? 'Re-add payment method' : 'Change payment method'}</ChangePaymentTitle>
          {paymentError && (
            <Alert ref={errorRef} key={paymentError.type}>
              <PaymentError error={paymentError} />
            </Alert>
          )}
          <RecurlyProvider publicKey={key}>
            <Elements>
              <PaymentSection>
                {showApplePay && (
                  <ApplePayOption disabled={processingMethodUpdate}>
                    <ApplePay isMethodChange onSuccess={handleSuccess} onError={handleFailure} />
                    <PaymentDivider>or</PaymentDivider>
                  </ApplePayOption>
                )}
                {showGooglePay && (
                  <GooglePayOption disabled={processingMethodUpdate}>
                    <GooglePay isMethodChange onSuccess={handleSuccess} onError={handleFailure} />
                    <PaymentDivider>or</PaymentDivider>
                  </GooglePayOption>
                )}
                <CardOptionTitle>Pay by card</CardOptionTitle>
              </PaymentSection>
              <PaymentMethod>
                <PaymentCardForm
                  innerRef={formRef}
                  customer={customer}
                  address={address}
                  onSubmitAttempt={handleSubmit}
                  onSuccess={handleSuccess}
                  onFailure={handleFailure}
                  onFormValidityChange={handleFormValidityChange}
                />
              </PaymentMethod>
              <PaymentChangeButtons>
                <MethodUpdateButton
                  buttonKind="secondaryBlue"
                  disabled={forceShow}
                  label="Cancel"
                  type="button"
                  onClick={handleCancel}
                />
                <MethodUpdateButton
                  data-automation-test-element="confirmation-method-change-button"
                  disabled={!isFormValid || processingMethodUpdate}
                  label={processingMethodUpdate ? <Spinner /> : 'Save card'}
                  buttonKind="primaryBlue"
                  type="submit"
                  form="recurly-payment-form"
                />
              </PaymentChangeButtons>
            </Elements>
          </RecurlyProvider>
        </div>
      )}
    </PaymentInformationContainer>
  );
};

PaymentMethodChange.propTypes = {
  forceShow: PropTypes.bool,
};

PaymentMethodChange.defaultProps = {
  forceShow: false,
};

export default PaymentMethodChange;
